import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  LifeBuoy,
  Brain,
  Calendar,
  HelpCircle,
  CheckCircle2,
  Activity,
  HeartPulse,
} from "lucide-react";

export default function StillLookingForDepressionHelpPage() {
  const publishedDate = "2024-11-15";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/11/still-looking-for-depression-help.jpg";

  const faqs = [
    {
      q: "What do I do if I've tried everything for depression and nothing works?",
      a: "If multiple antidepressants and therapy haven't provided relief, you may have treatment-resistant depression. Advanced interventional options like TMS, SPRAVATO®, IV Ketamine, and SAINT® accelerated TMS offer new hope with high remission rates.",
    },
    {
      q: "How fast can depression treatment start working?",
      a: "Traditional antidepressants take 4–8 weeks. TMS shows improvement around weeks 2–3. IV Ketamine can relieve symptoms within hours, and SAINT® accelerated TMS can achieve remission in just 5 days.",
    },
    {
      q: "Is it normal to still feel depressed after trying medication?",
      a: "Yes. Up to 30% of depression cases are treatment-resistant, meaning standard medications don't provide adequate relief. This is not a personal failure—it's a clinical signal that interventional treatments may be needed.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Still Looking for Depression Help? There Is New Hope | Neuro Wellness"
        description="If antidepressants and therapy haven't worked, you're not out of options. Discover TMS, SPRAVATO®, Ketamine, and SAINT®—advanced treatments for resistant depression."
        canonical="/still-looking-for-depression-help"
        keywords="still looking for depression help, treatment resistant depression, depression nothing works, TMS for depression, new depression treatments Coral Springs"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/still-looking-for-depression-help#webpage",
                name: "Still Looking for Depression Help?",
                url: "https://neurowellnesstms.com/still-looking-for-depression-help",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Hope-focused guide for patients with treatment-resistant depression, outlining advanced interventional options when standard treatments have failed.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/still-looking-for-depression-help#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <LifeBuoy className="h-4 w-4" />
                    Hope for Treatment-Resistant Depression
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Nov 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  Still Looking for{" "}
                  <span className="text-accent">Depression Help?</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  If medications and therapy haven't worked, you are not out of
                  options. Advanced interventional treatments offer new paths to
                  remission.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      You're Not Alone
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Find New Treatment Options
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS & ARTICLE */}
        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "Still Looking for Depression Help",
                  href: "/still-looking-for-depression-help",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                If you've tried multiple antidepressants, been to therapy, and
                still feel trapped by depression, it's natural to feel hopeless.
                But here's the truth: up to 30% of depression cases are{" "}
                <strong>treatment-resistant</strong>—and that resistance is not
                the end of the road. It's a signal that different, more advanced
                treatments are needed.
              </p>

              <h2>Why Standard Treatments Sometimes Fail</h2>
              <p>
                Traditional antidepressants target the monoamine system
                (serotonin, norepinephrine). But depression is complex,
                involving glutamate pathways, neuroinflammation, and disrupted
                brain circuitry. When monoamine-targeting meds fail, treatments
                that address these other mechanisms can succeed.
              </p>

              <h2>Advanced Options That Work When Meds Don't</h2>
              <ul>
                <li>
                  <strong>TMS Therapy:</strong> Non-invasive magnetic
                  stimulation of underactive brain regions. 60–70% response
                  rate.{" "}
                  <Link
                    to="/what-is-tms-therapy"
                    className="text-primary hover:underline font-semibold"
                  >
                    Learn more
                  </Link>
                  .
                </li>
                <li>
                  <strong>SPRAVATO® (esketamine):</strong> FDA-approved nasal
                  spray targeting glutamate. Insurance-covered.{" "}
                  <Link
                    to="/how-effective-is-spravato"
                    className="text-primary hover:underline font-semibold"
                  >
                    See efficacy
                  </Link>
                  .
                </li>
                <li>
                  <strong>IV Ketamine:</strong> Rapid-acting infusion, relief
                  within hours.{" "}
                  <Link
                    to="/intro-to-ketamine-therapy-coral-springs-fl"
                    className="text-primary hover:underline font-semibold"
                  >
                    Explore
                  </Link>
                  .
                </li>
                <li>
                  <strong>SAINT® Accelerated TMS:</strong> 5-day fMRI-guided
                  protocol, up to 79–90% remission.{" "}
                  <Link
                    to="/saint"
                    className="text-primary hover:underline font-semibold"
                  >
                    Discover SAINT®
                  </Link>
                  .
                </li>
              </ul>

              <h2>You Haven't Tried Everything Yet</h2>
              <p>
                If you've only tried oral medications and talk therapy, you
                haven't yet explored the interventional treatments that are
                changing lives. These aren't experimental—they're FDA-cleared,
                insurance-covered, and backed by rigorous clinical research.
                Relief is possible, and it may be closer than you think.
              </p>

              <div className="not-prose my-8 rounded-xl border border-amber-300/40 bg-amber-50 dark:bg-amber-950/30 p-5">
                <p className="text-sm text-foreground/90">
                  <strong>In crisis?</strong> If you're having thoughts of
                  self-harm, call or text <strong>988</strong> (Suicide & Crisis
                  Lifeline) immediately, or go to your nearest emergency room.
                </p>
              </div>
            </article>

            {/* FAQ SECTION */}
            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Hope & Recovery Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link
                    to="/whats-the-best-treatment-for-depression-a-simple-guide-to-understanding-your-options"
                    className="hover:underline"
                  >
                    → Best Treatment for Depression Guide
                  </Link>
                </li>
                <li>
                  <Link to="/what-is-tms-therapy" className="hover:underline">
                    → What Is TMS Therapy?
                  </Link>
                </li>
                <li>
                  <Link to="/saint" className="hover:underline">
                    → SAINT® 5-Day Accelerated TMS
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-to-get-mental-health-help"
                    className="hover:underline"
                  >
                    → How to Get Mental Health Help
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
